import dateConflict from './date-conflict';
import {
    analyticsRequestError,
    analyticsFormValidationError,
} from './adobe-analytics';
import {
    DATASET
} from './constants';

const emptyDateSelect = (dateSelect) => {
    dateSelect.querySelectorAll('option').forEach(option =>
        option.value !== '' && option.remove()
    );
};

const setHidden = (node, hidden) => {
    if (!node) {
        return;
    }

    const parentNode = node.hasAttribute(`${DATASET}-available-dates`) ?
        node.closest(`[${DATASET}-date]`) : node.parentElement;

    if (hidden) {
        node.setAttribute('hidden', 'hidden');
        // Force hide with CSS
        node.style.display = 'none';
    } else {
        node.removeAttribute('hidden');
        // Remove inline style to show
        node.style.display = '';
    }

    if (parentNode) {
        parentNode.setAttribute('data-has-hidden', hidden);
    }
};

const checkDelivery = (deliveryChoice, networkSwitchingForms) => {
    networkSwitchingForms.forEach((networkSwitchingForm) => {
        const dateSelect = networkSwitchingForm.querySelector(`[${DATASET}-available-dates]`);
        const isClickAndCollect = deliveryChoice.checked &&
            window.DELIVERY_CONSTANTS &&
            deliveryChoice.value === window.DELIVERY_CONSTANTS.CLICK_AND_COLLECT.toString();

        const clickAndCollectInfo = networkSwitchingForm
            .querySelector(`[${DATASET}-click-and-collect-info]`);

        networkSwitchingForm.setAttribute(
            `${DATASET}-delivery-click-and-collect`,
            isClickAndCollect
        );

        dateSelect
            .closest(`[${DATASET}-date]`)
            .setAttribute('data-has-disabled', isClickAndCollect);
        dateSelect.disabled = isClickAndCollect;

        setHidden(clickAndCollectInfo, !isClickAndCollect);
    });
};

const requestEndpoint = (url, method, body) => fetch(url, {
    method: method || 'GET',
    headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-Requested-With': 'XMLHttpRequest',
    },
    body: new URLSearchParams(body),
}).then(res => res.json());

const checkConsent = (networkSwitchingConsentInput, networkSwitchingContent, eventType) => {
    const consentChecked = networkSwitchingConsentInput.checked;
    setHidden(networkSwitchingContent, !consentChecked);
    if (!consentChecked || eventType === 'passive') {
        return;
    }
};

const toggleDisabledAttr = (node, condition) => {
    node.disabled = !condition;
    node.parentElement.setAttribute('data-has-disabled', !condition);
};

const updateLabels = (networkSwitchingFieldset, selectedOptionValue) => {
    const form = networkSwitchingFieldset.closest(`[${DATASET}]`);
    if (form) {
        form.dataset.networkSwitchingChoice = selectedOptionValue;
    }
    if (selectedOptionValue === 'no') {
        return;
    }
    const phoneNumberInput = networkSwitchingFieldset
        .querySelector(`[${DATASET}-phone-number]`);
    const phoneNumberLabel = networkSwitchingFieldset
        .querySelector(`label[for=${phoneNumberInput.id}]`);
    const consentCodeType = networkSwitchingFieldset
        .querySelector(`[${DATASET}-codetype]`);
    const switchingAction = networkSwitchingFieldset
        .querySelector(`[${DATASET}-action]`);
    const isPAC = selectedOptionValue === 'pac';
    const phoneNumberLabelSuffix = isPAC ? 'keep' : 'cancel';

    phoneNumberLabel.textContent = `Mobile number you wish to ${phoneNumberLabelSuffix}`;
    consentCodeType.textContent = selectedOptionValue.toUpperCase();
    switchingAction.textContent = isPAC ? 'switch' : 'cancel';
};

const setFieldsetsStates = (
    networkSwitchingRadioInputs,
    networkSwitchingFieldset,
    networkSwitchingConsentInput,
    networkSwitchingDateSelect,
    eventName
) => {

    const checkedRadioInput = [...networkSwitchingRadioInputs]
        .find(radioInput => radioInput.checked);
    const form = networkSwitchingFieldset.closest(`[${DATASET}]`);

    if (!checkedRadioInput) {
        return;
    }

    // Don't call form.reset() as it resets ALL forms in the basket
    // Instead, just ensure the checked radio is set
    checkedRadioInput.checked = true;
    checkedRadioInput
        .closest('.radio-selection-boolean')
        .classList.remove('is-invalid');

    bindConsentEvents(networkSwitchingFieldset, networkSwitchingConsentInput, 'passive');

    const radioRelatedInputs = networkSwitchingFieldset
        .querySelectorAll(`[${DATASET}-type]`);
    const radioRelatedInput = networkSwitchingFieldset.querySelector(
        `[${DATASET}-type="${checkedRadioInput.value}"]`
    );
    const formFeedback = form.querySelector(`[${DATASET}-feedback]`);

    toggleDisabledAttr(networkSwitchingFieldset, checkedRadioInput.value !== 'no');

    let enabledSelectDate = false;

    radioRelatedInputs.forEach((relatedInput) => {
        const isCurrentRadioRelatedInput = relatedInput === radioRelatedInput;
        toggleDisabledAttr(relatedInput, isCurrentRadioRelatedInput);
        if (isCurrentRadioRelatedInput && relatedInput.value !== '') {
            networkSwitchingConsentInput.checked = true;
            bindConsentEvents(networkSwitchingFieldset, networkSwitchingConsentInput, 'passive');
            if (networkSwitchingDateSelect.querySelectorAll('option').length > 1) {
                enabledSelectDate = true;
            }
        }
    });

    setFormState(form, enabledSelectDate ? 'edit' : 'validate');
    updateLabels(networkSwitchingFieldset, checkedRadioInput.value);
    setHidden(formFeedback, true);

    checkFormErrors(form, eventName);
};

const checkFormErrors = (form, eventName) => {
    let firstError = false;

    form.querySelectorAll(`.form-group, [${DATASET}-date-error]`).forEach((el) => {

        if (!el.classList.contains('is-invalid') || el.hasAttribute('hidden')) {
            return;
        }

        if (eventName === 'input') {
            el.classList.remove('is-invalid');
        } else if (!firstError) {
            firstError = true;
            const relNode = el.hasAttribute(`${DATASET}-date-error`) ?
                el.parentElement : el;
            window.scrollTo(0, relNode.getBoundingClientRect().top);
            analyticsRequestError(
                'Changed from Click and Collect to Home Delivery'
            );
        }
    });
};

const updateFeedback = (formFeedback, message) => {
    if (formFeedback) {
        formFeedback.innerHTML = message;
        setHidden(formFeedback, false);
    }
};

const setFormState = (form, state = 'validate') => {
    const submitButton = form.querySelector(`[${DATASET}-button]`);
    const editButton = form.querySelector(`[${DATASET}-edit-button]`);
    const inputs = form.querySelectorAll('input[type="text"]');
    const dateSelect = form.querySelector(`[${DATASET}-available-dates]`);
    const dateSelectContainer = dateSelect ? dateSelect.closest(`[${DATASET}-date]`) : null;
    const formFeedback = form.querySelector(`[${DATASET}-feedback]`);

    // Skip date-section toggling entirely for Click & Collect baskets: the
    // server-rendered cc-info message replaces the date picker.
    const isCcBasket = form.getAttribute('data-network-switching-cc') === 'true';

    // Show/hide buttons
    if (state === 'edit') {
        // After successful validation - show edit button, hide validate button
        setHidden(submitButton, true);
        setHidden(editButton, false);
        if (!isCcBasket) {
            // Show entire date container (including message and dropdown)
            if (dateSelectContainer) {
                setHidden(dateSelectContainer, false);
                dateSelectContainer.setAttribute('data-has-hidden', 'false');
            }
            // Explicitly ensure the date select itself is visible and enabled
            if (dateSelect) {
                dateSelect.removeAttribute('hidden');
                dateSelect.removeAttribute('data-has-hidden');
                dateSelect.style.display = '';
                dateSelect.disabled = false;
            }
        }
    } else {
        // Initial state or after clicking edit - show validate button, hide edit button
        setHidden(submitButton, false);
        setHidden(editButton, true);
        // Hide entire date container (including message and dropdown)
        if (dateSelectContainer) {
            setHidden(dateSelectContainer, true);
            dateSelectContainer.setAttribute('data-has-hidden', 'true');
        }
        // Clear date select and feedback when going back to validate mode
        emptyDateSelect(dateSelect);
        if (formFeedback) {
            formFeedback.innerHTML = '';
            setHidden(formFeedback, true);
        }
    }

    // Handle input readonly state
    inputs.forEach((input) => {
        if (state === 'edit') {
            // Make inputs readonly after successful validation
            input.setAttribute('readonly', 'readonly');
        } else {
            // Make inputs editable when in validate mode
            input.removeAttribute('readonly');
        }
    });

    form.dataset.networkSwitchingFormState = state;
};

const submitForm = (event, networkSwitchingDateSelect) => {
    const form = event.target;
    const formFeedback = form.querySelector(`[${DATASET}-feedback]`);
    const loader = document.querySelector('[data-loading-overlay]');
    const validateError = form.querySelector(`[${DATASET}-validate-error]`);
    const actionUrl = form.getAttribute('action');

    if (!actionUrl) {
        return;
    }

    event.preventDefault();
    if (loader) {
        loader.dataset.activeTasks = 1;
    }
    if (validateError) {
        setHidden(validateError, true);
    }
    if (formFeedback) {
        formFeedback.setAttribute('hidden', 'hidden');
    }

    // Manually create FormData since form might be a div
    const formData = new FormData();
    const inputs = form.querySelectorAll('input, select, textarea');
    inputs.forEach((input) => {
        if (input.name && !input.disabled) {
            if (input.type === 'checkbox' || input.type === 'radio') {
                if (input.checked) {
                    formData.append(input.name, input.value);
                }
            } else if (input.value) {
                formData.append(input.name, input.value);
            }
        }
    });

    requestEndpoint(
        actionUrl,
        form.getAttribute('method') || 'POST',
        formData
    )
        .then((data) => {
            if (data.shouldReloadPage) {
                return window.location.reload();
            }
            if (data.result === 'success') {
                populateAvailableDates(data.availableDates, networkSwitchingDateSelect);
                setFormState(form, 'edit');
            } else {
                throw new Error(data.message);
            }
        })
        .catch((error) => {
            updateFeedback(formFeedback, error.message);
            const dateSelectContainer = networkSwitchingDateSelect ? networkSwitchingDateSelect.closest(`[${DATASET}-date]`) : null;
            if (dateSelectContainer) {
                setHidden(dateSelectContainer, true);
            }
            analyticsRequestError(error.message);
        })
        .finally(() => loader.dataset.activeTasks = 0);
};

const bindConsentEvents = (networkSwitchingFieldset, networkSwitchingConsentInput, eventType) => {
    const networkSwitchingContent = networkSwitchingFieldset
        .querySelector(`[${DATASET}-content]`);

    if (!networkSwitchingConsentInput || !networkSwitchingContent) {
        return;
    }

    const inputListener = () => checkConsent(
        networkSwitchingConsentInput,
        networkSwitchingContent,
        eventType
    );

    networkSwitchingConsentInput.removeEventListener('input', inputListener);
    networkSwitchingConsentInput.addEventListener('input', inputListener);

    inputListener();
};

let checkedValidatePromotions = 'not-checked';

function validatePromotionsBasketOnCheckOut(e) {
    let returnData = 'true';
    if ($('.removePromotion').length) {
        let promoActionValidation = $('#revalidation').attr('href');
        let element = document.querySelector('.global-msg-boxes');
        if (promoActionValidation != '/validating' && checkedValidatePromotions == 'not-checked') {
            checkedValidatePromotions = 'checked';
            $('#revalidation').attr('href', '/validating');
            $.ajax({
                type: 'POST',
                url: promoActionValidation,
                async: false,
                context: window,
                dataType: 'JSON',
                success: function(res) {
                    if (res.result == 'success') {
                        $('.basketTotals').html(res.basketTotalsHtml);
                        $('.promoContent').html(res.promoAppliedHtml);
                        $('.global-msg-boxes').html('<div class="msg-box msg-box-warning"><span class="msg-box-icon"></span>Important: A promotion has been removed as it is no longer valid. Please check your basket for your updated payment total.</div>');
                        element.scrollIntoView(true);
                        e.stopImmediatePropagation();
                        e.preventDefault();
                        jQuery.each(res.basketLine, function(basketId, originalVal) {
                            $('.itemOriginalUpfront_' + basketId).text('');
                            $('.itemDiscountedUpfront_' + basketId).text(originalVal);
                            if ($('.itemDiscountedUpfront_' + basketId).hasClass('itemDiscountedUpfrontSingle')) {
                                $('.itemDiscountedUpfront_' + basketId).removeClass('itemDiscountedUpfrontSingle');
                            }
                            $('.itemPromoApplied_' + basketId).html('');
                            if ($('.itemPromoApplied_' + basketId).hasClass('accPromoApplied')) {
                                $('.itemPromoApplied_' + basketId).removeClass('mb-15');
                            }
                        });
                        returnData = 'false';
                    } else if (res.result != 'continue') {
                        $('.global-msg-boxes').html('<div class="msg-box msg-box-warning"><span class="msg-box-icon"></span>Important: A promotion has been removed as it is no longer valid. Please check your basket for your updated payment total.</div>');
                        element.scrollIntoView(true);
                        e.stopImmediatePropagation();
                        e.preventDefault();
                        returnData = 'false';
                    }
                },
                error: function() {
                    $('.global-msg-boxes').html('<div class="msg-box msg-box-warning"><span class="msg-box-icon"></span>A technical error has occured, please try again later.</div>');
                    element.scrollIntoView(true);
                    e.stopImmediatePropagation();
                    e.preventDefault();
                    setTimeout(function() {
                        window.location.reload();
                    }, 3000);
                    returnData = 'false';
                }
            });
        }
        $('#revalidation').attr('href', promoActionValidation);
    }
    return returnData;
}

function validateDealsOnCheckOut() {
    let returnData = 'true';
    $.ajax({
        type: 'POST',
        url: '/basket/deal/validation',
        async: false,
        dataType: 'JSON',
        success: function(res) {
            if (res.result == 'false') {
                returnData = 'false';
            }
        }
    });
    return returnData;
}

const checkValidityOnBasketSubmit = (e, networkSwitchingForms) => {
    // On /basket page the sidebar checkout buttons (.buttonSubmitSidebar) exist.
    // NS date validation runs on /basket/details — skip here so the form submits.
    if (document.querySelector('.buttonSubmitSidebar')) {
        return;
    }
    if (validateDealsOnCheckOut() == 'false') {
        window.location.reload();
    } else {
        let firstInvalidNode = null;
        if (validatePromotionsBasketOnCheckOut(e) == 'false') {
            return;
        }
        networkSwitchingForms.forEach((networkSwitchingForm) => {
            if (networkSwitchingForm.dataset.networkSwitchingChoice === 'no') {
                return;
            }
            const isValidateState = networkSwitchingForm.dataset.networkSwitchingFormState === 'validate';
            const validateErrorNode = networkSwitchingForm.querySelector(`[${DATASET}-validate-error]`);
            let firstErrorDisplayed = false;
            let invalidNode = null;
            networkSwitchingForm.querySelectorAll('.form-group').forEach((formGroup) => {
                const input = formGroup.querySelector('input') || formGroup.querySelector('select');
                if (!input) {
                    return;
                }
                if (!input.checkValidity() && !firstErrorDisplayed) {
                    formGroup.classList.add('is-invalid');
                    firstErrorDisplayed = true;
                    invalidNode = formGroup;
                    firstInvalidNode = firstInvalidNode || invalidNode;
                    analyticsFormValidationError(
                        [invalidNode.hasAttribute('data-analytic-name') ?
                            invalidNode.dataset.analyticName :
                            input.dataset.analyticName || input.getAttribute('name')
                        ]
                    );
                }
            });
            if ((!invalidNode || invalidNode && !!invalidNode.querySelector('[hidden]')) && isValidateState) {
                setHidden(validateErrorNode, false);
                invalidNode = validateErrorNode;
            }
        });
        if (firstInvalidNode) {
            e.stopImmediatePropagation();
            e.preventDefault();
            let scrollToNode = firstInvalidNode.closest('.card-section') || firstInvalidNode;
            scrollToNode.scrollIntoView(true);
            return false;
        }
    }
};

const onValidateClick = (form, networkSwitchingDateSelect) => {
    // Determine which radio option is selected
    const radioInputs = form.querySelectorAll(`[${DATASET}-decision] input[type="radio"]`);
    const checkedRadio = [...radioInputs].find(radio => radio.checked);
    const selectedType = checkedRadio ? checkedRadio.value : null;

    const invalidInputs = [];
    const inputs = form.querySelectorAll('input[required], select[required]');
    let isValid = true;
    inputs.forEach((input) => {
        // Skip validation for hidden fields or the date select (it gets populated after validation)
        // Also skip disabled fields as they're not relevant to the current selection
        if (input.hasAttribute('hidden') ||
            input.hidden ||
            input.disabled ||
            input.hasAttribute(`${DATASET}-available-dates`)) {
            return;
        }

        // Skip inputs that don't match the selected type (PAC/STAC codes)
        const inputType = input.getAttribute(`${DATASET}-type`);
        if (inputType && inputType !== selectedType) {
            return;
        }
        if (!input.checkValidity()) {
            input.parentElement.classList.add('is-invalid');
            invalidInputs.push(input.dataset.analyticName || input.getAttribute('name'));
            isValid = false;
        } else {
            input.parentElement.classList.remove('is-invalid');
        }
    });
    if (invalidInputs.length > 0) {
        analyticsFormValidationError(invalidInputs);
        return;
    }
    if (isValid) {
        // Create a synthetic event for submitForm
        const syntheticEvent = {
            target: form,
            // Empty function required for synthetic event
            preventDefault: () => { /* intentionally empty */ }
        };
        submitForm(syntheticEvent, networkSwitchingDateSelect);
    }
};

const networkSwitching = () => {
    dateConflict();

    const networkSwitchingForms = document.querySelectorAll(`[${DATASET}]`);

    if (!networkSwitchingForms.length) {
        return;
    }

    const basketFormButton = document
        .querySelector('#basket-form button[type="submit"]');

    networkSwitchingForms.forEach((networkSwitchingForm) => {
        const networkSwitchingRadioInputs = networkSwitchingForm
            .querySelectorAll(`[${DATASET}-decision] input[type="radio"]`);
        const networkSwitchingFieldset = networkSwitchingForm
            .querySelector(`[${DATASET}-fieldset]`);
        const networkSwitchingConsentInput = networkSwitchingFieldset
            .querySelector(`[${DATASET}-consent]`);
        const networkSwitchingDateSelect = networkSwitchingForm
            .querySelector(`[${DATASET}-available-dates]`);
        const networkSwitchingEditButton = networkSwitchingFieldset
            .querySelector(`[${DATASET}-edit-button]`);
        const networkSwitchingDateError = networkSwitchingFieldset
            .querySelector(`[${DATASET}-date-error]`);
        const codeInputs = networkSwitchingFieldset
            .querySelectorAll(`[${DATASET}-type]`);

        networkSwitchingRadioInputs.forEach((networkSwitchingRadioInput) => {
            networkSwitchingRadioInput.addEventListener('input', () => {
                setFieldsetsStates(
                    networkSwitchingRadioInputs,
                    networkSwitchingFieldset,
                    networkSwitchingConsentInput,
                    networkSwitchingDateSelect,
                    'input'
                );
            });
        });

        bindConsentEvents(networkSwitchingFieldset, networkSwitchingConsentInput);

        setFieldsetsStates(
            networkSwitchingRadioInputs,
            networkSwitchingFieldset,
            networkSwitchingConsentInput,
            networkSwitchingDateSelect,
            'load'
        );

        const validateButton = networkSwitchingForm.querySelector(`[${DATASET}-button]`);

        if (validateButton) {
            validateButton.addEventListener('click', () => onValidateClick(networkSwitchingForm, networkSwitchingDateSelect));
        }

        networkSwitchingForm.addEventListener('submit', e =>
            submitForm(e, networkSwitchingDateSelect)
        );

        networkSwitchingEditButton.addEventListener('click', () =>
            setFormState(networkSwitchingForm, 'validate')
        );

        // Ensure edit button is hidden on initial load
        if (networkSwitchingEditButton) {
            setHidden(networkSwitchingEditButton, true);
        }

        networkSwitchingDateSelect.addEventListener('input', () => {
            if (networkSwitchingDateSelect.value === '') {
                return;
            }

            if (networkSwitchingDateError) {
                networkSwitchingDateError.classList.remove('is-invalid');
            }
        });

        codeInputs.forEach(input => input.addEventListener('keyup', () =>
            input.value = input.value.toUpperCase()
        ));
    });

    const deliveryTypeChoices = document
        .querySelectorAll('[data-delivery-option-input]');

    deliveryTypeChoices.forEach((deliveryTypeChoice) => {
        deliveryTypeChoice.addEventListener('input', () =>
            checkDelivery(deliveryTypeChoice, networkSwitchingForms)
        );
        checkDelivery(deliveryTypeChoice, networkSwitchingForms);
    });

    if (basketFormButton) {
        basketFormButton.addEventListener('click', e =>
            checkValidityOnBasketSubmit(e, networkSwitchingForms)
        );
    }

    checkSwitchDateConflict();
};

const checkSwitchDateConflict = () => {
    const hash = document.location.hash;
    const dateSelect = document.querySelector(`[${DATASET}-available-dates]`);
    if (hash === '#switch-date-conflict' && dateSelect) {
        window.scrollTo(0, dateSelect.getBoundingClientRect().top);
        dateSelect.focus();
    }
};

const populateAvailableDates = (availableDates, networkSwitchingDateSelect) => {
    emptyDateSelect(networkSwitchingDateSelect);

    availableDates.map((availableDate) => {
        let option = document.createElement('option');
        option.value = availableDate.value;
        option.innerHTML = availableDate.label;
        networkSwitchingDateSelect.append(option);
    });

    // Remove selected attribute from placeholder option to prevent conflicts
    // Do this after adding options
    const placeholderOption = networkSwitchingDateSelect.querySelector('option[value=""]');
    if (placeholderOption) {
        placeholderOption.removeAttribute('selected');
        placeholderOption.selected = false;
    }

    // Clear any validation errors when dates are loaded
    const formGroup = networkSwitchingDateSelect.closest('.form-group');
    const dateContainer = networkSwitchingDateSelect.closest('[data-network-switching-date]');
    if (formGroup) {
        formGroup.classList.remove('is-invalid');
    }
    if (dateContainer) {
        const helpBlock = dateContainer.querySelector('.help-block');
        if (helpBlock) {
            helpBlock.classList.remove('is-invalid');
            helpBlock.style.display = 'none';
        }
    }
};

export default networkSwitching;
